import { useEffect, useRef } from 'react';
import type { JSX } from 'react';
import type { AppIconType } from '../hooks/useAppIcon';
import { getIconPath } from '../hooks/useAppIcon';

export interface IconSettingsModalProps {
  readonly currentIcon: AppIconType;
  readonly onSelect: (icon: AppIconType) => void;
  readonly onClose: () => void;
}

const ICON_OPTIONS: AppIconType[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'icon1', 'icon2'];

export function IconSettingsModal(props: IconSettingsModalProps): JSX.Element {
  const { currentIcon, onSelect, onClose } = props;
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Use native <dialog> methods for proper modal behavior (focus trap, backdrop)
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) {
      dialog.showModal();
    }
    return () => {
      if (dialog && dialog.open) {
        dialog.close();
      }
    };
  }, []);

  // Close on backdrop click
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };

    const handleClick = (e: MouseEvent) => {
      if (e.target === dialog) {
        onClose();
      }
    };

    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('click', handleClick);
    return () => {
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('click', handleClick);
    };
  }, [onClose]);

  return (
    <dialog
      ref={dialogRef}
      className="icon-settings-modal"
      aria-label="アイコン設定"
    >
      <div className="icon-settings-modal__content">
        <h2 className="icon-settings-modal__title">ホーム画面アイコンの設定</h2>
        <p className="icon-settings-modal__desc">
          「ホーム画面に追加」したときに表示されるアイコンを選択してください。
        </p>
        <div className="icon-settings-modal__grid">
          {ICON_OPTIONS.map((opt) => (
            <button
              key={opt}
              type="button"
              className={`icon-settings-modal__item ${
                currentIcon === opt ? 'icon-settings-modal__item--selected' : ''
              }`}
              onClick={() => onSelect(opt)}
              aria-label={`アイコン ${opt}`}
              aria-pressed={currentIcon === opt}
            >
              <img
                src={getIconPath(opt)}
                alt={`アイコン ${opt}`}
                className="icon-settings-modal__img"
                /* 表示サイズと同じ width/height を明示してレイアウトシフトを防ぐ。
                 * 元画像は 256x256 PNG (再エンコード済) で 1 枚 ~100KB だが、
                 * decoding=async で UI スレッドをブロックしないようにする。
                 * 全アイコンが同時に必要なので lazy は使わず eager で読み込む。 */
                width={64}
                height={64}
                decoding="async"
                loading="eager"
              />
            </button>
          ))}
        </div>
        <div className="icon-settings-modal__actions">
          <button
            type="button"
            className="icon-settings-modal__close"
            onClick={onClose}
          >
            閉じる
          </button>
        </div>
      </div>
    </dialog>
  );
}
